// ScalingPlanPage — Marketing's "Scaling Plan" page
// More profitable = more buying power. Stay consistent, accumulate profits, trade more contracts.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TfCard, TfCardFloating, TfSectionHeaderView, TfStatRow, TfTimeRange, TfProgress } from '../../components/TfPrimitives';

interface ScaleStep {
  level: number;
  profitMilestone: number;       // cumulative profit in $
  buyingPower: number;            // total account size in $K
  newContracts: string;
  reward: string;
  perks: string[];
}

const SCALE: ScaleStep[] = [
  { level: 1, profitMilestone: 0,        buyingPower: 100, newContracts: '10 Standard / 100 Micros',  reward: '1.5% × Profit', perks: ['Daily rewards enabled after 3 payouts'] },
  { level: 2, profitMilestone: 5000,     buyingPower: 125, newContracts: '12 Standard / 125 Micros',  reward: '1.5% × Profit', perks: ['Priority support', '0.5% performance bonus'] },
  { level: 3, profitMilestone: 15000,    buyingPower: 150, newContracts: '15 Standard / 150 Micros',  reward: '2% × Profit',   perks: ['L2 market data free', 'Bot marketplace access'] },
  { level: 4, profitMilestone: 35000,    buyingPower: 200, newContracts: '20 Standard / 200 Micros',  reward: '2% × Profit',   perks: ['API access', 'Dedicated account manager'] },
  { level: 5, profitMilestone: 75000,    buyingPower: 250, newContracts: '25 Standard / 250 Micros',  reward: '2.5% × Profit', perks: ['Custom strategy', '1:1 coaching'] },
  { level: 6, profitMilestone: 150000,   buyingPower: 300, newContracts: '30 Standard / 300 Micros',  reward: '3% × Profit',   perks: ['VIP Slack channel', 'Equity partnership talks'] },
];

export function ScalingPlanPage() {
  const nav = useNavigate();
  const [currentLevel] = useState(2);
  const [range, setRange] = useState('1M');

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Scaling plan</div>
        <div className="w-9" />
      </div>

      {/* Hero — white floating card with current buying power + change */}
      <div className="mx-4 mt-4">
        <TfCardFloating className="text-center">
          <div className="text-[13px]" style={{ color: '#8E8E93' }}>Funded amount</div>
          <div className="mt-1 flex items-baseline justify-center gap-1">
            <span className="text-[40px] font-bold tabular-nums leading-none" style={{ color: '#1C1C1E' }}>$125,000</span>
          </div>
          <div className="mt-1 flex items-center justify-center gap-1 text-[13px]">
            <span className="font-semibold text-tf-profit">+25.0%</span>
            <span className="text-tf-text-3">(+$25,000)</span>
            <span className="text-tf-text-3">· Today</span>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <button onClick={() => nav('/portal/challenge')} className="flex h-12 items-center justify-center gap-1.5 rounded-full bg-tf-text-1 text-[15px] font-semibold text-white active:scale-[0.97] transition-transform">
              Level up
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 17L17 7" /><path d="M7 7h10v10" />
              </svg>
            </button>
            <button onClick={() => nav('/portal/payouts')} className="flex h-12 items-center justify-center gap-1.5 rounded-full bg-tf-text-1 text-[15px] font-semibold text-white active:scale-[0.97] transition-transform">
              Withdraw
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 7L7 17" /><path d="M17 17H7V7" />
              </svg>
            </button>
          </div>
        </TfCardFloating>
      </div>

      {/* Time range pills */}
      <TfTimeRange value={range} onChange={setRange} className="mt-3" />

      {/* Stat row */}
      <TfStatRow
        stats={[
          { label: 'Current level', value: 'L2', color: '#FF9500' },
          { label: 'Next at', value: '$15K', color: '#1C1C1E' },
          { label: 'Progress', value: '35%', color: '#34C759' },
        ]}
      />

      {/* Current level progress */}
      <div className="mx-4 mt-3">
        <TfCard className="p-4">
          <div className="flex items-center justify-between mb-1">
            <div className="text-[11px] uppercase tracking-wider text-tf-text-3">Next level at</div>
            <div className="text-[12px] font-bold tabular-nums text-tf-orange">$15,000</div>
          </div>
          <div className="text-[18px] font-bold text-tf-text-1">$5,240 / $15,000 <span className="text-[12px] text-tf-text-3 font-normal">cumulative</span></div>
          <div className="mt-2">
            <TfProgress value={35} color="var(--tf-orange)" />
          </div>
          <p className="mt-2 text-[11px] text-tf-text-3">$9,760 more profit to unlock Level 3 ($150K account)</p>
        </TfCard>
      </div>

      {/* Levels */}
      <TfSectionHeaderView title="All levels" viewAllLabel="View all" onViewAll={() => {}} />
      <div className="mx-4">
        <TfCard>
          {SCALE.map((s, i) => {
            const isCurrent = s.level === currentLevel;
            const isPast = s.level < currentLevel;
            return (
              <div
                key={s.level}
                data-testid={`scale-l${s.level}`}
                className={['flex items-center gap-3 px-4 py-3', i < SCALE.length - 1 ? 'border-b border-tf-border' : '', isCurrent ? 'bg-tf-orange/[0.06]' : ''].join(' ')}
              >
                <div className={['flex h-12 w-12 shrink-0 items-center justify-center rounded-[12px] text-[16px] font-black',
                  isPast ? 'bg-tf-green-tint text-tf-profit' : isCurrent ? 'bg-tf-orange text-white' : 'bg-tf-surface-1 text-tf-text-3'
                ].join(' ')}>
                  {isPast ? '✓' : s.level}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[14px] font-bold text-tf-text-1">${s.buyingPower}K account</span>
                    {isCurrent && <span className="rounded-full bg-tf-orange px-1.5 py-0.5 text-[8px] font-bold uppercase text-white">Current</span>}
                  </div>
                  <div className="text-[11px] text-tf-text-3">From ${s.profitMilestone.toLocaleString()} cumulative profit</div>
                  <div className="text-[10px] text-tf-text-3 mt-0.5">{s.newContracts} · {s.reward}</div>
                </div>
                <svg viewBox="0 0 24 24" className="h-4 w-4 text-tf-text-muted" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </div>
            );
          })}
        </TfCard>
      </div>

      {/* Perks unlocked */}
      <TfSectionHeaderView title="Perks by level" />
      <div className="mx-4 space-y-1.5">
        {SCALE.flatMap((s) => s.perks.map((p, i) => ({ perk: p, level: s.level, key: `${s.level}-${i}` }))).map((x) => (
          <div key={x.key} data-testid={`perk-${x.level}-${x.perk}`} className={['flex items-center gap-2 rounded-[12px] bg-white p-2.5', x.level > currentLevel ? 'opacity-50' : ''].join(' ')} style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
            <span className="text-[18px]">{x.level <= currentLevel ? '🔓' : '🔒'}</span>
            <div className="flex-1 text-[12px] font-medium text-tf-text-1">{x.perk}</div>
            <span className="rounded-full bg-tf-surface-1 px-1.5 py-0.5 text-[9px] font-bold uppercase text-tf-text-3">L{x.level}</span>
          </div>
        ))}
      </div>

      {/* How it works */}
      <TfSectionHeaderView title="How scaling works" />
      <div className="mx-4">
        <TfCard>
          {[
            'Cumulative profit = sum of all your net payouts',
            'Reach a milestone → account automatically upgrades within 24h',
            'New buying power applied to all your funded accounts',
            'No action required — handled by our system',
            'Downgrades never happen (you can only move up)',
          ].map((step, i, a) => (
            <div key={i} className={['flex items-start gap-2.5 px-4 py-3', i < a.length - 1 ? 'border-b border-tf-border' : ''].join(' ')}>
              <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-tf-orange text-white text-[10px] font-bold">{i + 1}</div>
              <span className="text-[13px] text-tf-text-1 leading-relaxed">{step}</span>
            </div>
          ))}
        </TfCard>
      </div>
    </div>
  );
}
